import { Group, Menu, Popover, Select, Tooltip } from '@mantine/core';
import { IconDotsVertical, IconPencil, IconSettings, IconTrashX } from '@tabler/icons-react';
import { ColDef } from 'ag-grid-community';
import { useCallback, useMemo, useState } from 'react';
import { useApi } from 'api/api-context';
import P2Regular from 'components/typography/P2Regular';
import Button from 'components/Button';
import ActionIcon from 'components/ActionIcon';
import DataTable from 'components/tables/DataTable';
import EvidenceFieldApplicabilitySelectFilter from 'components/tables/filters/EvidenceFieldApplicabilitySelectFilter';
import EvidenceFieldSectionTypeSelectFilter from 'components/tables/filters/EvidenceFieldSectionTypeSelectFilter';
import Toast from 'components/Toast';
import { useConfirm } from 'components/modals/message/MessageProvider';
import {
  getResponsiveActionsColumnWidth,
  SINGLE_ACTIONS_COLUMN_WIDTH,
  SINGLE_ACTION_BUTTON_WIDTH,
  useResponsiveDesign,
} from 'hooks/use-responsive-design';
import { Link } from 'react-router-dom';
import useLocalStorage from 'hooks/use-local-storage';
import FormPresentationShell from 'components/forms/FormPresentationShell';
import EvidenceFieldSectionFormPage from 'pages/devices-module/evidence-field/EvidenceFieldSectionFormPage';
import { ADD_EVIDENCE_FIELD_SECTION_PAGE_PATH, EDIT_EVIDENCE_FIELD_SECTION_PAGE_PATH } from 'routes/paths';
import {
  DEPARTMENT_FORM_MODE_OPTIONS,
  isDepartmentFormMode,
} from 'components/tables/data/department/form-presentation';
import { EvidenceSection } from 'components/forms/evidence-field/use-evidence-sections';

/** Administers module-specific sections using the evidence-field permission boundary. */
export default function EvidenceFieldSectionsAdmin() {
  const { getAction } = useApi();
  const { confirm } = useConfirm();
  const { tableActionMode, isMobile } = useResponsiveDesign();
  const [revision, setRevision] = useState(0);
  const [savedPresentation, , updatePresentation] = useLocalStorage(
    'objekto.evidence-field-sections.form-presentation',
    {
      mode: 'same-tab',
    }
  );
  const presentationMode = isDepartmentFormMode(savedPresentation.mode) ? savedPresentation.mode : 'same-tab';
  const overlayMode = presentationMode === 'drawer' || presentationMode === 'modal';
  const linkTarget = presentationMode === 'new-tab' ? '_blank' : undefined;
  const [overlay, setOverlay] = useState<{ section?: EvidenceSection } | null>(null);
  const [overlayDirty, setOverlayDirty] = useState(false);
  const [overlayBusy, setOverlayBusy] = useState(false);
  const closeOverlay = useCallback(() => {
    setOverlay(null);
    setOverlayDirty(false);
    setOverlayBusy(false);
  }, []);
  const openOverlay = useCallback((section?: EvidenceSection) => {
    setOverlay({ section });
    setOverlayDirty(false);
    setOverlayBusy(false);
  }, []);
  const [error, setError] = useState('');
  const load = useCallback(async () => {
    return getAction('DeviceListEvidenceFieldSections')({});
  }, [getAction]);
  /** Rejects nonempty deletion and reloads the catalog after success. */
  const remove = async (section: EvidenceSection) => {
    try {
      await getAction('DeviceDeleteEvidenceFieldSection')({ parameters: { sectionId: String(section.sectionId) } });
      setRevision((value) => value + 1);
    } catch {
      setError('Sekciu sa nepodarilo odstrániť. Najskôr presuňte všetky jej evidenčné polia do inej sekcie.');
    }
  };
  const columns = useMemo<ColDef[]>(
    () => [
      {
        field: 'name',
        headerName: 'Názov',
        flex: 1,
        minWidth: 200,
        sortable: true,
        unSortIcon: true,
        autoHeaderHeight: true,
        cellRenderer: ({ value }: { value: string }) => <P2Regular>{value}</P2Regular>,
      },
      {
        field: 'applicability',
        headerName: 'Použitie',
        minWidth: 180,
        filter: true,
        floatingFilter: true,
        floatingFilterComponent: EvidenceFieldApplicabilitySelectFilter,
        autoHeaderHeight: true,
        valueGetter: ({ data }) => data.module,
        valueFormatter: ({ value }) => (value === 'people' ? 'Osoby' : 'Zariadenia'),
        cellRenderer: ({ value }: { value: string }) => (
          <P2Regular>{value === 'people' ? 'Osoby' : 'Zariadenia'}</P2Regular>
        ),
      },
      {
        field: 'order',
        headerName: 'Poradie',
        width: 120,
        sortable: true,
        unSortIcon: true,
        autoHeaderHeight: true,
        cellRenderer: ({ value }: { value: number }) => <P2Regular>{value}</P2Regular>,
      },
      {
        field: 'builtinKey',
        headerName: 'Typ',
        width: 180,
        filter: true,
        floatingFilter: true,
        floatingFilterComponent: EvidenceFieldSectionTypeSelectFilter,
        autoHeaderHeight: true,
        valueGetter: ({ data }) => (data.builtinKey ? 'builtin' : 'custom'),
        cellRenderer: ({ value }: { value: string }) => (
          <P2Regular>{value === 'builtin' ? 'Predvolená sekcia' : 'Vlastná sekcia'}</P2Regular>
        ),
        valueFormatter: ({ value }) => (value === 'builtin' ? 'Predvolená sekcia' : 'Vlastná sekcia'),
      },
      {
        field: '_actions',
        headerName: 'Akcie',
        pinned: 'right',
        lockPosition: 'right',
        width: getResponsiveActionsColumnWidth(SINGLE_ACTIONS_COLUMN_WIDTH, tableActionMode),
        minWidth: getResponsiveActionsColumnWidth(SINGLE_ACTIONS_COLUMN_WIDTH, tableActionMode),
        maxWidth: getResponsiveActionsColumnWidth(SINGLE_ACTIONS_COLUMN_WIDTH, tableActionMode),
        autoHeaderHeight: true,
        cellRenderer: ({ data }: { data: EvidenceSection }) => (
          <Group spacing={12} noWrap>
            {tableActionMode === 'desktop' && (
              <Button
                w={SINGLE_ACTION_BUTTON_WIDTH}
                size="md"
                variant="secondary"
                leftIcon={<IconPencil />}
                component={Link}
                to={`${EDIT_EVIDENCE_FIELD_SECTION_PAGE_PATH.insert({ sectionId: data.sectionId })}${
                  linkTarget ? '?presentation=new-tab' : ''
                }`}
                target={linkTarget}
                rel={linkTarget ? 'noopener noreferrer' : undefined}
                onClick={(event) => {
                  if (overlayMode) {
                    event.preventDefault();
                    openOverlay(data);
                  }
                }}
              >
                Upraviť
              </Button>
            )}
            {tableActionMode === 'tablet' && (
              <Tooltip label="Upraviť" events={{ hover: true, focus: true, touch: true }}>
                <ActionIcon
                  aria-label="Upraviť"
                  variant="secondary"
                  size="md"
                  component={Link}
                  to={`${EDIT_EVIDENCE_FIELD_SECTION_PAGE_PATH.insert({ sectionId: data.sectionId })}${
                    linkTarget ? '?presentation=new-tab' : ''
                  }`}
                  target={linkTarget}
                  rel={linkTarget ? 'noopener noreferrer' : undefined}
                  onClick={(event) => {
                    if (overlayMode) {
                      event.preventDefault();
                      openOverlay(data);
                    }
                  }}
                >
                  <IconPencil />
                </ActionIcon>
              </Tooltip>
            )}
            {(isMobile || !data.builtinKey) && (
              <Menu withinPortal position="bottom-end">
                <Menu.Target>
                  <ActionIcon variant="tertiary" size={isMobile ? 'sm' : 'md'} aria-label="Ďalšie akcie sekcie">
                    <IconDotsVertical stroke={1.5} />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Label>
                    <P2Regular>{data.name}</P2Regular>
                  </Menu.Label>
                  {isMobile && (
                    <Menu.Item
                      icon={<IconPencil size={20} stroke={1.5} />}
                      component={Link}
                      to={`${EDIT_EVIDENCE_FIELD_SECTION_PAGE_PATH.insert({ sectionId: data.sectionId })}${
                        linkTarget ? '?presentation=new-tab' : ''
                      }`}
                      target={linkTarget}
                      rel={linkTarget ? 'noopener noreferrer' : undefined}
                      onClick={(event) => {
                        if (overlayMode) {
                          event.preventDefault();
                          openOverlay(data);
                        }
                      }}
                    >
                      Upraviť
                    </Menu.Item>
                  )}
                  {!data.builtinKey && (
                    <Menu.Item
                      color="red.8"
                      icon={<IconTrashX size={20} stroke={1.5} />}
                      onClick={() =>
                        confirm({
                          title: 'Odstrániť sekciu',
                          content: `Odstrániť sekciu „${data.name}“? Sekcia musí byť prázdna.`,
                          primaryButtonText: 'Odstrániť',
                          secondaryButtonText: 'Zrušiť',
                          onConfirm: () => remove(data),
                        })
                      }
                    >
                      Odstrániť
                    </Menu.Item>
                  )}
                </Menu.Dropdown>
              </Menu>
            )}
          </Group>
        ),
      },
    ],
    [getAction, confirm, tableActionMode, isMobile, linkTarget, overlayMode, openOverlay]
  );
  return (
    <>
      {error && <Toast type="fail" message={error} withCloseButton={false} />}
      <DataTable
        tableId="evidence-field-sections"
        title="Sekcie polí"
        addButtonText="Pridať sekciu"
        addButtonTarget={
          overlayMode
            ? undefined
            : `${ADD_EVIDENCE_FIELD_SECTION_PAGE_PATH.original}${linkTarget ? '?presentation=new-tab' : ''}`
        }
        addButtonLinkTarget={linkTarget}
        onAddButtonClick={overlayMode ? () => openOverlay() : undefined}
        addButtonActions={
          <Popover position="top-end" width={240} withinPortal withArrow>
            <Popover.Target>
              <ActionIcon size="md" variant="primary" aria-label="Nastavenia formulára" title="Nastavenia formulára">
                <IconSettings size={18} stroke={1.5} />
              </ActionIcon>
            </Popover.Target>
            <Popover.Dropdown>
              <Select
                label="Spôsob otvorenia formulára"
                data={DEPARTMENT_FORM_MODE_OPTIONS}
                value={presentationMode}
                withinPortal={false}
                onChange={(value) => {
                  if (isDepartmentFormMode(value)) updatePresentation({ mode: value });
                }}
              />
            </Popover.Dropdown>
          </Popover>
        }
        columns={columns}
        action={load}
        dataRefreshToken={revision}
        hideToggleDiscarded
      />
      {overlay && overlayMode && (
        <FormPresentationShell
          mode={presentationMode === 'drawer' ? 'drawer' : 'modal'}
          title={overlay.section ? `Úprava sekcie: ${overlay.section.name}` : 'Pridanie sekcie'}
          dirty={overlayDirty}
          busy={overlayBusy}
          onClose={closeOverlay}
        >
          {(requestClose) => (
            <EvidenceFieldSectionFormPage
              embedded
              edit={Boolean(overlay.section)}
              sectionIdOverride={overlay.section ? String(overlay.section.sectionId) : undefined}
              onSaved={() => setRevision((value) => value + 1)}
              onComplete={closeOverlay}
              onClose={requestClose}
              onDirtyChange={setOverlayDirty}
              onBusyChange={setOverlayBusy}
            />
          )}
        </FormPresentationShell>
      )}
    </>
  );
}
